import React, { useEffect, useState } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { BsClockHistory, BsX, BsDownload, BsArrowClockwise } from 'react-icons/bs';
import { getFileMergeSchedulerLogDetail, downloadFullS3File } from '../../../../Redux/sftpClient/action';
import { handleDownloadFileUrl } from '../../Dashboard/functions';
import { Tooltip } from '@mui/material';


const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";
const ACCENT = '#6ba2b6';
const ACCENT_DARK = '#4f7f91';

const statusBadgeStyles = {
    COMPLETED: { bg: '#ecfdf5', color: '#047857', border: '#a7f3d0' },
    IN_PROCESS: { bg: '#fffbeb', color: '#b45309', border: '#fde68a' },
    PENDING: { bg: '#f0f9ff', color: '#0369a1', border: '#bae6fd' },
    FAILED: { bg: '#fff1f2', color: '#be123c', border: '#fecdd3' },
};

const StatusBadge = ({ status }) => {
    if (!status) return <span>—</span>;
    const { bg, color, border } = statusBadgeStyles[status] || { bg: '#f8fafc', color: '#475569', border: '#e2e8f0' };
    return (
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                backgroundColor: bg,
                color,
                border: `1px solid ${border}`,
                fontSize: '11px',
                fontWeight: 500,
                padding: '2px 9px',
                borderRadius: '999px',
                whiteSpace: 'nowrap',
            }}
        >
            {String(status).replace('_', ' ').replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())}
        </span>
    );
};

// Highlights a leading "[tag]" prefix, or a trailing filename, so log lines scan like a real log viewer.
const renderComment = (comment) => {
    if (!comment) return '—';
    const text = String(comment);

    const tagMatch = text.match(/^\[([^\]]+)\]\s*(.*)$/);
    if (tagMatch) {
        return (
            <>
                <span style={{ color: '#4f46e5', fontWeight: 600 }}>[{tagMatch[1]}]</span>{' '}
                <span style={{ color: '#475569' }}>{tagMatch[2]}</span>
            </>
        );
    }

    const fileMatch = text.match(/^(.*?)(\S+\.\w{2,5})$/);
    if (fileMatch && fileMatch[1]) {
        return (
            <>
                <span>{fileMatch[1]}</span>
                <span style={{ color: '#0f172a', fontWeight: 600 }}>{fileMatch[2]}</span>
            </>
        );
    }

    return text;
};

const normalizeLogs = (response) => {
    if (!response) return [];
    const payload = response?.data !== undefined ? response.data : response;
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.results?.data)) return payload.results.data;
    if (Array.isArray(payload?.results)) return payload.results;
    if (payload && typeof payload === 'object') return [payload];
    return [];
};

const DetailedLoggingModal = ({ show, onClose, id }) => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [downloadingLogId, setDownloadingLogId] = useState(null);

    const fetchLogs = () => {
        if (!id) return;
        setLoading(true);
        getFileMergeSchedulerLogDetail(id)((response) => {
            setLogs(normalizeLogs(response));
            setLoading(false);
        });
    };

    useEffect(() => {
        if (!show || !id) return;
        fetchLogs();
    }, [show, id]);

    const handleClose = () => {
        onClose();
        setLogs([]);
    };

    const handleDownloadLogFile = (log, index) => {
        const rowKey = log?.id ?? index;
        setDownloadingLogId(rowKey);
        downloadFullS3File({ file_path: log?.log_record || "" })((response) => {
            setDownloadingLogId(null);
            if (response?.status && response?.data?.signed_url) {
                handleDownloadFileUrl(response.data.signed_url);
            }
        });
    };

    return (
        <Modal show={show} onHide={handleClose} centered size="xl" backdrop="static" contentClassName="border-0 shadow-lg overflow-hidden" style={{ borderRadius: '12px' }}>
            <style>{`
                .dlm-table {
                    table-layout: fixed;
                    border-collapse: collapse;
                    width: 100%;
                }
                .dlm-table thead th {
                    position: sticky;
                    top: 0;
                    z-index: 1;
                }
                .dlm-table tbody tr {
                    border-bottom: 1px solid #f1f5f9;
                    transition: background-color 0.12s ease;
                }
                .dlm-table tbody tr:last-child {
                    border-bottom: none;
                }
                .dlm-table tbody tr:hover {
                    background-color: rgba(248, 250, 252, 0.8);
                }
                .dlm-table th,
                .dlm-table td {
                    padding: 12px 16px;
                }
                .dlm-close-btn {
                    color: #ffffff;
                    background: rgba(255,255,255,0.14);
                    border: none;
                    transition: background-color 0.15s ease;
                }
                .dlm-close-btn:hover {
                    background-color: rgba(255,255,255,0.28);
                }
                .dlm-close-btn:disabled {
                    opacity: 0.6;
                    cursor: default;
                }
                .dlm-spin {
                    animation: dlm-spin-anim 0.8s linear infinite;
                }
                @keyframes dlm-spin-anim {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                .dlm-scroll {
                    scrollbar-width: thin;
                    scrollbar-color: ${ACCENT} #f1f5f9;
                }
                .dlm-scroll::-webkit-scrollbar {
                    width: 8px;
                    height: 8px;
                }
                .dlm-scroll::-webkit-scrollbar-track {
                    background: #f1f5f9;
                }
                .dlm-scroll::-webkit-scrollbar-thumb {
                    background-color: ${ACCENT};
                    border-radius: 8px;
                }
                .dlm-scroll::-webkit-scrollbar-thumb:hover {
                    background-color: ${ACCENT_DARK};
                }
            `}</style>

            <div style={{ background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_DARK} 100%)`, color: '#ffffff', padding: '20px 24px' }} className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-3">
                    <div
                        className="d-flex align-items-center justify-content-center"
                        style={{ width: 40, height: 40, borderRadius: '12px', backgroundColor: 'rgba(255,255,255,0.16)' }}
                    >
                        <BsClockHistory color="#ffffff" size={18} />
                    </div>
                    <div>
                        <div className="fw-bold" style={{ fontSize: '16px', lineHeight: 1.2 }}>Detailed Logging</div>
                        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.75)' }}>
                            {logs.length} log{logs.length !== 1 ? 's' : ''} recorded for this job
                        </div>
                    </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <button
                        title="Refresh"
                        onClick={fetchLogs}
                        disabled={loading}
                        className="dlm-close-btn d-flex align-items-center justify-content-center"
                        style={{ width: 30, height: 30, borderRadius: '50%' }}
                    >
                        <BsArrowClockwise size={16} className={loading ? 'dlm-spin' : ''} />
                    </button>
                    <button onClick={handleClose} className="dlm-close-btn d-flex align-items-center justify-content-center" style={{ width: 30, height: 30, borderRadius: '50%' }}>
                        <BsX size={20} />
                    </button>
                </div>
            </div>

            <div className="dlm-scroll" style={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto', overflowX: 'auto', backgroundColor: '#ffffff' }}>
                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" style={{ color: ACCENT_DARK }} />
                    </div>
                ) : logs.length === 0 ? (
                    <div className="text-center text-muted py-5">
                        <BsClockHistory size={26} color="#cbd5e1" />
                        <div className="mt-2" style={{ fontSize: '13px' }}>No log entries to display</div>
                    </div>
                ) : (
                    <table className="dlm-table" style={{ fontSize: '12px', minWidth: '820px' }}>
                        <colgroup>
                            <col style={{ width: '56px' }} />
                            <col style={{ width: '116px' }} />
                            <col />
                            <col style={{ width: '112px' }} />
                            <col style={{ width: '180px' }} />
                            <col style={{ width: '65px' }} />
                        </colgroup>
                        <thead>
                            <tr style={{ backgroundColor: '#f4f8f9', borderBottom: `2px solid ${ACCENT}` }}>
                                <th style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left' }}>S.NO</th>
                                <th style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left' }}>Status</th>
                                <th style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left' }}>Comment</th>
                                <th style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left' }}>Staff Name</th>
                                <th style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left' }}>Created Date</th>
                                <th style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left' }}>Log</th>
                            </tr>
                        </thead>
                        <tbody>
                            {logs.map((log, index) => (
                                <tr key={log?.id ?? index}>
                                    <td style={{ color: '#94a3b8', fontFamily: MONO, verticalAlign: 'top' }}>{index + 1}</td>
                                    <td style={{ verticalAlign: 'top' }}><StatusBadge status={log?.status} /></td>
                                    <Tooltip
                                        title={`${log?.comment || ''}${log?.job_number ? ` | Job Number : ${log.job_number}` : ''}`}
                                        arrow
                                        placement="top"
                                    >
                                        <td
                                            style={{
                                                color: '#1e293b',
                                                fontFamily: MONO,
                                                lineHeight: 1.6,
                                                maxWidth: '500px',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                wordBreak: 'break-word',
                                                cursor: 'help',
                                            }}
                                            title={`${log?.comment || ''}${log?.job_number ? ` | Job Number : ${log.job_number}` : ''}`}
                                        >
                                            {renderComment(log?.comment)}

                                            {log?.job_number && (
                                                <>
                                                    <span style={{ color: '#cbd5e1' }}> | </span>
                                                    <span style={{ color: '#475569' }}>
                                                        Job Number : {log.job_number}
                                                    </span>
                                                </>
                                            )}
                                        </td>
                                    </Tooltip>
                                    <td style={{ color: '#475569', fontWeight: 500, verticalAlign: 'top', whiteSpace: 'nowrap' }}>{log?.staff_name || '—'}</td>
                                    <td style={{ color: '#94a3b8', fontFamily: MONO, verticalAlign: 'top', whiteSpace: 'nowrap' }}>{log?.created_date || '—'}</td>
                                    <td style={{ verticalAlign: 'top' }}>
                                        {log?.log_record && (
                                            <button
                                                title="Download Log File"
                                                onClick={() => !downloadingLogId && handleDownloadLogFile(log, index)}
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: 26,
                                                    height: 26,
                                                    borderRadius: '6px',
                                                    border: 'none',
                                                    backgroundColor: 'transparent',
                                                }}
                                            >
                                                {downloadingLogId === (log?.id ?? index) ? (
                                                    <Spinner animation="border" size="sm" style={{ width: 14, height: 14, color: ACCENT_DARK }} />
                                                ) : (
                                                    <BsDownload color={ACCENT_DARK} size={15} />
                                                )}
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </Modal>
    );
};

export default DetailedLoggingModal;
